// Copyright (c) Move Closer 2022.

import { RoutesFactory } from '@movecloser/front-core'
import { RouteConfig } from 'vue-router'

import { buildStaticRoutesDefinitions, StaticRouteDefinition } from '../../contexts'
import { authConfig, authSSOParamsKey, authSSOSelectedProvider, authSSOSource } from './config/auth'

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
export enum RouteNames {
  Auth = 'auth',
  CompanyCreate = 'company-create',
  Consents = 'consents',
  SSOAuth = 'ssoAuth',
  SSOMiddleware = 'ssoMiddleware',
  ResetPassword = 'reset-password',
  RequestResetPassword = 'request-reset-password',
  SignUpWelcome = 'signup-welcome'
}

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
export const authModuleStaticRoutes: StaticRouteDefinition[] = buildStaticRoutesDefinitions(
  'auth',
  Object.values(RouteNames)
)

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
export const routesFactory: RoutesFactory = (): RouteConfig[] => {
  return [
    {
      path: '/auth',
      name: RouteNames.Auth,
      meta: { auth: false, metaTitle: 'Authorization', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/Auth/Auth.vue'),
      beforeEnter: (to, from, next) => {
        // TODO: Very, very weak guard :( (Should be fixed)
        if (typeof window === 'undefined' || !window.localStorage.getItem(authConfig.tokenName)) {
          next()
          return
        }

        next({ name: 'profile.me' })
      }
    },
    {
      path: '/auth/sso',
      name: RouteNames.SSOAuth,
      meta: { auth: false, metaTitle: 'SSO Auth', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/SSOMiddleware.vue'),
      beforeEnter: (to, from, next) => {
        if (process.env.VUE_APP_TYPE !== 'backoffice') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (!process.client || !window) {
            next()
            return
          }
        } else {
          if (typeof window === 'undefined') {
            next()
            return
          }
        }

        if (!window.localStorage.getItem(authSSOSelectedProvider)) {
          next()
          return
        }
        window.localStorage.setItem(authSSOParamsKey, JSON.stringify(to.query))
        const source = window.localStorage.getItem(authSSOSource)
        if (!source) {
          next({ name: 'auth.auth' })
          return
        }

        next({ name: 'auth.ssoMiddleware' })
      }
    },
    {
      path: '/company-create',
      name: RouteNames.CompanyCreate,
      meta: { auth: true, metaTitle: 'Create company', static: true },
      component: () => import(/* webpackChunkName: "profile/CompanyCreateView" */ './views/CompanyCreate/CompanyCreateView.vue')
    },
    {
      path: '/sso-middleware',
      name: RouteNames.SSOMiddleware,
      meta: { auth: false, metaTitle: 'SSO Auth', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/SSOMiddleware.vue'),
      beforeEnter: (to, from, next) => {
        if (process.env.VUE_APP_TYPE !== 'backoffice') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (!process.client || typeof window === 'undefined') {
            next({ name: 'auth.auth' })
            return
          }
        } else {
          if (typeof window === 'undefined') {
            next({ name: 'auth.auth' })
            return
          }
        }

        if (!window.localStorage.getItem(authSSOSelectedProvider)) {
          next({ name: 'auth.auth' })
          return
        }

        next()
      }
    },
    {
      path: '/auth/consents',
      name: RouteNames.Consents,
      meta: { auth: false, metaTitle: 'Consents request', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/ConsentsRequest/ConsentsRequest.vue'),
      beforeEnter: (to, from, next) => {
        if (typeof window === 'undefined') {
          next()
          return
        }

        if (!window.localStorage.getItem(authSSOParamsKey)) {
          next({ name: 'auth.auth' })
          return
        }

        next()
      }
    },
    {
      path: '/reset-password',
      name: RouteNames.ResetPassword,
      meta: { auth: false, metaTitle: 'Reset Password', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/ResetPassword.vue')
    },
    {
      path: '/request-reset-password',
      name: RouteNames.RequestResetPassword,
      meta: { auth: false, metaTitle: 'Request Password', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/RequestResetPassword.vue')
    },
    {
      path: '/signup-welcome',
      name: RouteNames.SignUpWelcome,
      meta: { auth: false, metaTitle: 'Signup Welcome', static: true },
      component: () => import(/* webpackChunkName: "profile/view" */ './views/SignUpWelcome/SignUpWelcome.vue')
    }
  ]
}
